import {
  ApiRef,
  DiscoveryApi,
  FetchApi,
  createApiRef,
} from '@backstage/frontend-plugin-api';
import {
  AssistantEvent,
  ChatMessage,
  ExecuteRequest,
  ExecuteResponse,
  PageContext,
  RunStatus,
} from '@internal/plugin-assistant-common';

export interface AssistantApi {
  /** Streams one chat turn. Resolves when the stream ends. */
  chat(
    messages: ChatMessage[],
    onEvent: (event: AssistantEvent) => void,
    signal?: AbortSignal,
    page?: PageContext,
  ): Promise<void>;
  execute(request: ExecuteRequest): Promise<ExecuteResponse>;
  /** One run's status and new log, read as the user. */
  run(id: string, tenant?: string, since?: number): Promise<RunStatus>;
}

export const assistantApiRef: ApiRef<AssistantApi> = createApiRef<AssistantApi>(
  { id: 'plugin.assistant.service' },
);

/**
 * Split an SSE buffer into complete events. Returns the unparsed tail, which
 * belongs to an event still arriving.
 */
export function parseSse(buffer: string): {
  events: AssistantEvent[];
  rest: string;
} {
  const normalized = buffer.replace(/\r\n/g, '\n');
  const blocks = normalized.split('\n\n');
  const rest = blocks.pop() ?? '';
  const events: AssistantEvent[] = [];
  for (const block of blocks) {
    const data = block
      .split('\n')
      .filter(line => line.startsWith('data:'))
      .map(line => line.slice(5).trimStart())
      .join('\n');
    if (!data) continue;
    try {
      events.push(JSON.parse(data));
    } catch {
      // A malformed event is skipped, not fatal.
    }
  }
  return { events, rest };
}

async function errorMessage(response: Response): Promise<string> {
  const text = await response.text();
  try {
    const message = JSON.parse(text)?.error?.message;
    if (typeof message === 'string') return message;
  } catch {
    // fall through
  }
  return text || `HTTP ${response.status}`;
}

export class AssistantClient implements AssistantApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async chat(
    messages: ChatMessage[],
    onEvent: (event: AssistantEvent) => void,
    signal?: AbortSignal,
    page?: PageContext,
  ): Promise<void> {
    const base = await this.discoveryApi.getBaseUrl('assistant');
    // fetchApi adds the user's Backstage token as the bearer.
    const response = await this.fetchApi.fetch(`${base}/chat`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'text/event-stream',
      },
      body: JSON.stringify(page ? { messages, page } : { messages }),
      signal,
    });
    if (!response.ok) {
      onEvent({ type: 'error', message: await errorMessage(response) });
      onEvent({ type: 'done' });
      return;
    }
    const reader = response.body?.getReader?.();
    if (!reader) {
      parseSse(`${await response.text()}\n\n`).events.forEach(onEvent);
      return;
    }
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parsed = parseSse(buffer);
      buffer = parsed.rest;
      parsed.events.forEach(onEvent);
    }
    parseSse(`${buffer}\n\n`).events.forEach(onEvent);
  }

  async execute(request: ExecuteRequest): Promise<ExecuteResponse> {
    const base = await this.discoveryApi.getBaseUrl('assistant');
    const response = await this.fetchApi.fetch(`${base}/execute`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      // Our own validation failed (bad name, missing typed confirmation).
      const body = await errorMessage(response);
      return { ok: false, status: response.status, body };
    }
    return (await response.json()) as ExecuteResponse;
  }

  async run(id: string, tenant?: string, since = 0): Promise<RunStatus> {
    const base = await this.discoveryApi.getBaseUrl('assistant');
    const query = new URLSearchParams({ since: String(since) });
    if (tenant) query.set('tenant', tenant);
    const response = await this.fetchApi.fetch(
      `${base}/runs/${encodeURIComponent(id)}?${query}`,
    );
    if (!response.ok) return { ok: false, httpStatus: response.status, id };
    return (await response.json()) as RunStatus;
  }
}
